import { Close, DragIndicator } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useState } from 'react';

import { useTranslations } from 'context/TranslationContext';

import { validateIban } from 'utils/helpers/invoiceHelpers';
import { CounterPartyIbanFormValue } from 'utils/interfaces/CounterParty';

import {
  ibanHintStyle,
  ibanPrimaryBadgeStyle,
  ibanRowDragOverStyle,
  ibanRowDraggedStyle,
  ibanRowPrimaryStyle,
  ibanRowStyle
} from 'styles/components/SlideOverStyle';

interface IbanSectionProps {
  readonly ibans: CounterPartyIbanFormValue[];
  readonly onAdd: (iban: string) => void;
  readonly onRemove: (index: number) => void;
  readonly onMove: (from: number, to: number) => void;
}

const normalizeIban = (iban: string) => iban.replace(/\s+/g, '').toUpperCase();

export const IbanSection = ({
  ibans,
  onAdd,
  onRemove,
  onMove
}: IbanSectionProps) => {
  const { translate } = useTranslations();

  const [newIban, setNewIban] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const trimmedIban = newIban.trim();
  const isDuplicate = ibans.some(
    (iban) => normalizeIban(iban.iban) === normalizeIban(trimmedIban)
  );
  const isInvalid = trimmedIban.length > 0 && !validateIban(trimmedIban);
  const canAdd =
    trimmedIban.length > 0 && validateIban(trimmedIban) && !isDuplicate;

  const caption = isInvalid
    ? translate('errors.invalidIban')
    : isDuplicate
      ? translate('errors.ibanUnique')
      : '';

  const handleAdd = () => {
    if (!canAdd) return;
    onAdd(trimmedIban);
    setNewIban('');
  };

  const handleDragStart = (index: number) => (event: React.DragEvent) => {
    event.dataTransfer.setData('text/plain', String(index));
    event.dataTransfer.effectAllowed = 'move';
    setDraggedIndex(index);
  };

  const handleDragOver = (index: number) => (event: React.DragEvent) => {
    event.preventDefault();
    if (index !== draggedIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => setDragOverIndex(null);

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (index: number) => (event: React.DragEvent) => {
    event.preventDefault();
    const source = Number.parseInt(
      event.dataTransfer.getData('text/plain'),
      10
    );
    if (!Number.isNaN(source) && source !== index) {
      onMove(source, index);
    }
    handleDragEnd();
  };

  return (
    <Stack spacing={1}>
      {ibans.map((iban, index) => (
        <Box
          key={iban.tmpId}
          draggable
          data-testid={`iban-row-${index}`}
          onDragStart={handleDragStart(index)}
          onDragOver={handleDragOver(index)}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop(index)}
          onDragEnd={handleDragEnd}
          sx={{
            ...ibanRowStyle,
            ...(index === 0 && ibanRowPrimaryStyle),
            ...(draggedIndex === index && ibanRowDraggedStyle),
            ...(dragOverIndex === index && ibanRowDragOverStyle)
          }}
        >
          <DragIndicator
            fontSize="small"
            sx={{ color: '#9ca3af', cursor: 'grab' }}
          />
          {index === 0 && (
            <Chip
              size="small"
              label={translate('addCounterparty.primary')}
              sx={ibanPrimaryBadgeStyle}
            />
          )}
          <Typography sx={{ flexGrow: 1, wordBreak: 'break-all' }}>
            {iban.iban}
          </Typography>
          <Tooltip title={iban.isUsed ? translate('messages.ibanIsUsed') : ''}>
            <span>
              <IconButton
                type="button"
                size="small"
                aria-label={translate('labels.deleteIban')}
                disabled={!!iban.isUsed}
                onClick={() => onRemove(index)}
              >
                <Close fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ))}

      {ibans.length > 0 && (
        <Typography sx={ibanHintStyle}>
          {translate('addCounterparty.dragToReorder')}
        </Typography>
      )}

      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          fullWidth
          value={newIban}
          error={!!caption}
          helperText={caption}
          placeholder={translate('addCounterparty.enterIban')}
          sx={{ textAlign: 'left' }}
          onChange={(event) => setNewIban(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleAdd();
            }
          }}
        />
        <Button
          type="button"
          variant="contained"
          disabled={!canAdd}
          onClick={handleAdd}
          sx={{ height: '3.5rem', whiteSpace: 'nowrap' }}
        >
          {translate('addCounterparty.addIban')}
        </Button>
      </Stack>
    </Stack>
  );
};
